import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';
import { NgApexchartsModule } from 'ng-apexcharts';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexTooltip,
  ApexStroke,
  ApexNonAxisChartSeries,
  ApexResponsive,
  ApexLegend,
  ApexPlotOptions,
  ApexFill,
  ApexYAxis,
  ApexGrid
} from 'ng-apexcharts';
import { DashboardService } from '../../../../core/services/dashboard.service';
import { OrderService } from '../../../../core/services/order.service';
import { FormsModule } from '@angular/forms';
import { AlertService } from '../../../../shared/components/alert/alert';
import { TranslationService } from '../../../../core/services/translation.service';
import { NotificationService } from '../../../../core/services/notification.service';

export type ChartOptions = {
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  dataLabels: ApexDataLabels;
  responsive: ApexResponsive[];
  labels: any;
  legend: ApexLegend;
  plotOptions: ApexPlotOptions;
  fill: ApexFill;
  yaxis: ApexYAxis;
  grid: ApexGrid;
  colors: string[];
};

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, TranslatePipe, NgApexchartsModule, FormsModule],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css',
})
export class Dashboard implements OnInit {
  protected readonly window = window;
  
  selectedYear: string = '2026';
  years: string[] = ['2026', '2025', '2024'];
  
  revenueChart!: Partial<ChartOptions>;
  ticketOverviewChart!: Partial<ChartOptions>;
  orderOverviewChart!: Partial<ChartOptions>;
  ordersBarChart!: Partial<ChartOptions>;
  
  orderFilter: string = 'All';
  recentOrders: any[] = [];
  monthlyOrderStats: any = null;
  recentOrdersSearch: string = '';
  isRecentOrdersLoading: boolean = false;
  
  totalRevenue: number = 0;
  totalTickets: number = 0;
  totalOrders: number = 0;
  
  isSaving: boolean = false;
  isModalOpen: boolean = false;
  editingId: string | null = null;
  newOrder: any = {
    status: ''
  };
  
  private destroy$ = new Subject<void>();

  private searchSubject = new Subject<string>();
  
  constructor(
    private dashboardService: DashboardService,
    private orderService: OrderService,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private translationService: TranslationService,
    private notificationService: NotificationService
  ) {}

  

  ngOnInit(): void {
    this.initCharts();
    this.loadData();
    this.loadRecentOrders();

    this.searchSubject.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(term => {
      this.recentOrdersSearch = term;
      this.loadRecentOrders();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData() {
    const yearNum = parseInt(this.selectedYear);

    this.dashboardService.getTicketRevenue(yearNum).subscribe({
      next: (res) => {
        if (res && res.returncode === '300' && res.data) {
          const data = res.data;
          const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
          
          const dataArray = monthKeys.map(key => {
            const val = data[key.toLowerCase()];
            return val !== undefined && val !== null ? Number(val) : 0;
          });
          
          const maxVal = Math.max(...dataArray);
           let dynamicMax: number | undefined;
          if (maxVal > 0) {
            if (maxVal <= 10) dynamicMax = Math.ceil(maxVal);
            else if (maxVal <= 100) dynamicMax = Math.ceil(maxVal / 10) * 10;
            else if (maxVal <= 1000) dynamicMax = Math.ceil(maxVal / 100) * 100;
            else dynamicMax = Math.ceil(maxVal / 2000) * 2000;
          } else {
            dynamicMax = 4000;
          }
          
          this.revenueChart.series = [{
            name: 'Revenue',
            data: dataArray
          }];
          
          this.totalRevenue = dataArray.reduce((acc, val) => acc + val, 0);
          
          this.revenueChart.yaxis = {
            min: 0,
            max: dynamicMax,
            tickAmount: dynamicMax <= 10 ? dynamicMax : 5,
            labels: {
              formatter: (val) => val ? val.toLocaleString() : '0'
            }
          };
          this.revenueChart = { ...this.revenueChart };
          this.cdr.detectChanges();
        } else {
          console.warn('Ticket Revenue API returned non-300 or missing data:', res);
        }
      },
      error: (err) => {
        console.error('Ticket Revenue API ERROR:', err);
      }
    });

    this.dashboardService.getTicketCounts(yearNum).subscribe({
      next: (res) => {
        if (res.returncode === '300' && res.data) {
          const { open, pending, confirmed, failed } = res.data;
          this.totalTickets = Number(open || 0) + Number(pending || 0) + Number(confirmed || 0) + Number(failed || 0);
          this.ticketOverviewChart.series = [open, pending, confirmed, failed];
          this.ticketOverviewChart = { ...this.ticketOverviewChart };
          this.cdr.detectChanges();
        }
      },
      error: (err) => console.error('Ticket Counts API error:', err)
    });

    this.dashboardService.getOrderCounts(yearNum).subscribe({
      next: (res) => {
        if (res && res.returncode === '300' && res.data) {
          const { open, confirmed, failed } = res.data;
          this.totalOrders = Number(open || 0) + Number(confirmed || 0) + Number(failed || 0);
          this.orderOverviewChart.series = [Number(open || 0), Number(confirmed || 0), Number(failed || 0)];
          this.orderOverviewChart = { ...this.orderOverviewChart };
          this.cdr.detectChanges();
        } else {
          console.warn('Order Counts API returned non-300 or missing data:', res);
        }
      },
      error: (err) => console.error('Order Counts API ERROR:', err)
    });

    this.dashboardService.getMonthlyOrders(yearNum).subscribe({
      next: (res) => {
        if (res && res.returncode === '300' && res.data) {
          this.monthlyOrderStats = res.data;
          this.applyOrderFilter();
        }
      },
      error: (err) => console.error('Monthly Orders API ERROR:', err)
    });
  }

  loadRecentOrders() {
    this.isRecentOrdersLoading = true;
    const payload: any = { page: '1', limit: '10' };
    if (this.recentOrdersSearch) {
      payload.search = this.recentOrdersSearch;
    }

     this.orderService.getAllOrders(payload).subscribe({
      next: (res) => {
        if (res.returncode === '300') {
          this.recentOrders = res.data.data;
          this.cdr.detectChanges();
        }
        this.isRecentOrdersLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading recent orders:', err);
        this.isRecentOrdersLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onSearch(event: any) {
    const term = event.target.value;
    this.isRecentOrdersLoading = true;
    this.cdr.detectChanges();
    this.searchSubject.next(term);
  }

  onYearChange() {
    this.loadData();
  }

  onOrderFilterChange(filter: string) {
    this.orderFilter = filter;
    this.applyOrderFilter();
  }

  private applyOrderFilter() {
    if (!this.monthlyOrderStats) return;

    const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    let dataObj: any;

    switch (this.orderFilter) {
      case 'Pending': dataObj = this.monthlyOrderStats.pending; break;
      case 'Confirmed': dataObj = this.monthlyOrderStats.confirmed; break;
      case 'Failed': dataObj = this.monthlyOrderStats.failed; break;
      default: dataObj = this.monthlyOrderStats.all; break;
    }

    if (dataObj) {
      const dataArray = monthKeys.map(key => dataObj[key.toLowerCase()] || 0);
      
      const maxVal = Math.max(...dataArray);
      
      let dynamicMax: number | undefined;
      if (maxVal > 0) {
        if (maxVal <= 10) dynamicMax = Math.ceil(maxVal);
        else if (maxVal <= 100) dynamicMax = Math.ceil(maxVal / 10) * 10;
        else if (maxVal <= 1000) dynamicMax = Math.ceil(maxVal / 100) * 100;
        else dynamicMax = Math.ceil(maxVal / 2000) * 2000;
      } else {
        dynamicMax = 2000;
      }

      this.ordersBarChart.series = [{ name: 'Orders', data: dataArray }];
      this.ordersBarChart = { 
        ...this.ordersBarChart,
        yaxis: {
          min: 0,
          max: dynamicMax,
          tickAmount: dynamicMax <= 10 ? dynamicMax : 5,
          labels: {
            formatter: (val) => val ? Math.floor(val).toLocaleString() : '0'
          }
        }
      };
      this.cdr.detectChanges();
    }
  }

  private initCharts() {
    this.revenueChart = {
      series: [{ name: 'Revenue', data: Array(12).fill(0) }],
      chart: { 
        height: 300, 
        type: 'area', 
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: true },
        fontFamily: "'Inter', sans-serif"
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 3 },
      xaxis: {
        categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: {
        min: 0,
        labels: {
          formatter: (val) => val ? val.toLocaleString() : '0'
        }
      },
      colors: ['#1f3f66'],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.5,
          opacityTo: 0.1,
          stops: [0, 90, 100]
        }
      },
      grid: {
        borderColor: '#f1f5f9',
        strokeDashArray: 4
      }
    };

    this.ticketOverviewChart = {
      series: [0, 0, 0, 0],
      chart: { type: 'donut', height: 250 },
      labels: ['Open', 'Pending', 'Confirmed', 'Failed'],
      colors: ['#ffc107', '#ff9800', '#1f3f66', '#dc3545'],
      plotOptions: {
        pie: {
          donut: {
            size: '75%',
            labels: {
              show: true,
              total: {
                show: false,
                label: 'Total',
                formatter: (w) => {
                  return w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0).toString();
                }
              }
            }
          },
          dataLabels: {
            offset: 25
          }
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function (val: any, opts: any) {
          return opts.w.config.series[opts.seriesIndex];
        },
        style: {
          colors: ['#333'],
          fontSize: '14px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600
        },
        dropShadow: {
          enabled: false
        }
      },
      legend: { 
        position: 'bottom',
        offsetY: 10
      },
      responsive: [{ breakpoint: 480, options: { chart: { width: 200 }, legend: { position: 'bottom' } } }]
    };

    this.orderOverviewChart = {
      series: [0, 0, 0],
      chart: { 
        type: 'donut', 
        height: 280,
        offsetY: 20
      },
      plotOptions: {
        pie: {
          donut: {
            size: '75%',
            labels: {
              show: true,
              total: {
                show: false,
                label: 'Total',
                formatter: (w) => {
                  return w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0).toString();
                }
              }
            }
          },
          dataLabels: {
            offset: 25
          }
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function (val: any, opts: any) {
          return opts.w.config.series[opts.seriesIndex];
        },
        style: {
          colors: ['#333'],
          fontSize: '14px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600
        },
        dropShadow: {
          enabled: false
        }
      },
      labels: ['Pending', 'Confirmed', 'Failed'],
      colors: ['#ff9800', '#1f3f66', '#dc3545'],
      legend: { 
        position: 'bottom',
        offsetY: 10
      }
    };

    this.ordersBarChart = {
      series: [{ name: 'Orders', data: Array(12).fill(0) }],
      chart: { 
        type: 'bar', 
        height: 300, 
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      plotOptions: { bar: { columnWidth: '45%', borderRadius: 4 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      },
      colors: ['#1f3f66'],
      grid: { borderColor: '#f1f1f1' },
      yaxis: {
        min: 0,
        max: 5,
        tickAmount: 5,
        labels: {
          formatter: (val) => val ? Math.floor(val).toLocaleString() : '0'
        }
      }
    };
  }

  private updateCharts(stats: any) {
    this.cdr.detectChanges();
  }

  onEditOrder(order: any) {
    this.editingId = order.id;
    this.isModalOpen = true;
    this.newOrder = {
      status: order.status
    };
  }

  closeModal() {
    this.isModalOpen = false;
    this.cdr.detectChanges();
  }

  submitOrder() {
    if (!this.editingId) return;
    
    this.isSaving = true;
    const payload = {
      id: this.editingId,
      status: this.newOrder.status
    };

    this.orderService.updateOrder(payload).subscribe({
      next: (res: any) => {
        this.isSaving = false;
        if (res.returncode === '300') {
          this.alertService.success(this.translationService.translate('alert.updateSuccess'));
          this.closeModal();
          this.loadRecentOrders();
          this.loadData(); 
        } else {
          this.alertService.error(res.message || this.translationService.translate('alert.updateError'));
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        this.isSaving = false;
        console.error('Error updating order status:', err);
        this.alertService.error(this.translationService.translate('alert.errorOccurred'));
        this.cdr.detectChanges();
      }
    });
  }

  formatValue(val: number): string {
    if (val < 1000) return val.toLocaleString();
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(val).toLowerCase();
  }
}
